import { useAuth } from "@babackapp/auth-client/hooks";
import { useGetShopMembers } from "@babackapp/private-api-client/client/use-get-shop-members";
import { useInvalidateReturnRequest } from "@babackapp/private-api-client/client/use-invalidate-return-request";
import useResendReturnRequestEmail from "@babackapp/private-api-client/client/use-resend-return-request-email";
import useResendReturnRequestEmailToMember from "@babackapp/private-api-client/client/use-resend-return-request-email-to-member";
import { ReturnRequestLogDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Checkbox } from "@babackapp/ui/components/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { Input } from "@babackapp/ui/components/input";
import { Label } from "@babackapp/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { SendIcon, TriangleAlertIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { isDegradedResend, RESEND_EMAIL_COOLDOWN_MS } from "./resend-email";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type ResendEmailDialogProps = {
  activity: ReturnRequestLogDto;
  returnRequestId: string;
  /** Baback team: free recipient, may skip the cooldown. */
  isSuperAdmin: boolean;
  /** Date of the latest resend of this entry, if any. */
  lastResendAt?: number;
};

/**
 * Resend of an email activity entry (BBS-354). Superadmins type any address;
 * shop admins/owners pick a shop member. Both default to the viewer's own
 * address and share a 5-minute cooldown per entry.
 */
export default function ResendEmailDialog({
  activity,
  returnRequestId,
  isSuperAdmin,
  lastResendAt,
}: ResendEmailDialogProps) {
  const t = useTranslation();
  const { user } = useAuth();
  const { data: shop } = useCurrentShopSuspense();
  const { data: members = [] } = useGetShopMembers(shop.id);
  const resendAsSuperAdmin = useResendReturnRequestEmail(shop.id);
  const resendToMember = useResendReturnRequestEmailToMember(shop.id);
  const invalidateReturnRequest = useInvalidateReturnRequest();

  const viewerEmail = user?.emails[0]?.address ?? "";
  const memberEmails = useMemo(
    () =>
      members
        .map((member) => member.user?.email)
        .filter((email): email is string => !!email),
    [members],
  );
  // The member list holds the stored casing: match it, or the select opens
  // without a selection when the login email differs only by case.
  const defaultEmail = isSuperAdmin
    ? viewerEmail
    : (memberEmails.find(
        (memberEmail) =>
          memberEmail.toLowerCase() === viewerEmail.toLowerCase(),
      ) ?? viewerEmail);

  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(viewerEmail);
  const [shouldSkipCooldown, setShouldSkipCooldown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // Keeps the cooldown countdown current while the dialog is open.
  useEffect(() => {
    if (!open) return;
    setNow(Date.now());
    const interval = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(interval);
  }, [open]);

  const remainingCooldownMs = lastResendAt
    ? lastResendAt + RESEND_EMAIL_COOLDOWN_MS - now
    : 0;
  const isCoolingDown =
    remainingCooldownMs > 0 && !(isSuperAdmin && shouldSkipCooldown);

  const isMemberEmail = memberEmails.some(
    (memberEmail) => memberEmail.toLowerCase() === email.trim().toLowerCase(),
  );
  const shouldShowDegradedNotice = isDegradedResend(activity);
  const isPending = resendAsSuperAdmin.isPending || resendToMember.isPending;

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      setEmail(defaultEmail);
      setShouldSkipCooldown(false);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!EMAIL_REGEX.test(email.trim())) {
      setError(t("activities.email.resendInvalidEmail"));
      return;
    }
    setError(null);
    try {
      if (isSuperAdmin) {
        await resendAsSuperAdmin.mutateAsync({
          returnRequestId,
          logId: activity.id,
          email: email.trim(),
          shouldSkipCooldown,
        });
      } else {
        await resendToMember.mutateAsync({
          returnRequestId,
          logId: activity.id,
          email,
        });
      }
      setOpen(false);
      invalidateReturnRequest({ shopId: shop.id, returnRequestId });
    } catch (mutationError) {
      setError(getResendErrorMessage(mutationError));
    }
  };

  // The hooks skip the generic error toast: say what actually went wrong.
  const getResendErrorMessage = (mutationError: unknown): string => {
    const { error: code, retryAfterSeconds } = (mutationError ?? {}) as {
      error?: string;
      retryAfterSeconds?: number;
    };
    switch (code) {
      case "RESEND_EMAIL_COOLDOWN":
        return t("activities.email.resendCooldown", {
          minutes: Math.max(1, Math.ceil((retryAfterSeconds ?? 60) / 60)),
        });
      case "RESEND_EMAIL_RECIPIENT_NOT_MEMBER":
        return t("activities.email.resendRecipientNotMember");
      case "RESEND_EMAIL_NOT_RESENDABLE":
        return t("activities.email.resendNotResendable");
      default:
        return t("activities.email.resendFailed");
    }
  };

  const trigger = (
    <DialogTrigger asChild>
      <button
        type="button"
        className={
          isSuperAdmin
            ? "flex items-center gap-1 rounded-md border border-amber-400 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-50 transition-colors"
            : "flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors"
        }
      >
        <SendIcon className="h-4 w-4" />
        {isSuperAdmin
          ? t("activities.email.resend")
          : t("activities.email.resendToMember")}
      </button>
    </DialogTrigger>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {isSuperAdmin ? (
        <div className="mt-2 flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
          <span className="text-xs text-amber-700 bg-amber-100 rounded px-2 py-0.5 font-medium">
            Baback CSM
          </span>
          {trigger}
        </div>
      ) : (
        <div className="mt-2 flex flex-col items-start gap-1">
          <p className="text-xs text-slate-500">
            {t("activities.email.resendHint")}
          </p>
          {trigger}
        </div>
      )}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("activities.email.resendDialogTitle")}</DialogTitle>
        </DialogHeader>
        {shouldShowDegradedNotice && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            {t("activities.email.resendDegradedSnapshot")}
          </p>
        )}
        <div className="space-y-2">
          <Label>{t("activities.email.resendRecipientLabel")}</Label>
          {isSuperAdmin ? (
            <Input
              type="email"
              value={email}
              placeholder={t("activities.email.resendPlaceholder")}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError(null);
              }}
            />
          ) : (
            <Select value={email} onValueChange={setEmail}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {memberEmails.map((memberEmail) => (
                  <SelectItem key={memberEmail} value={memberEmail}>
                    {memberEmail}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {isSuperAdmin && email.trim() !== "" && !isMemberEmail && (
            <p className="flex items-center gap-1.5 text-xs text-amber-700">
              <TriangleAlertIcon className="size-3.5 shrink-0" />
              {t("activities.email.resendNotAMemberWarning")}
            </p>
          )}
        </div>
        {isSuperAdmin && remainingCooldownMs > 0 && (
          <div className="flex items-center gap-3">
            <Checkbox
              id={`resend-skip-cooldown-${activity.id}`}
              checked={shouldSkipCooldown}
              onCheckedChange={(checked) =>
                setShouldSkipCooldown(checked === true)
              }
            />
            <label
              htmlFor={`resend-skip-cooldown-${activity.id}`}
              className="text-sm cursor-pointer"
            >
              {t("activities.email.resendSkipCooldown")}
            </label>
          </div>
        )}
        {isCoolingDown && (
          <p className="text-xs text-slate-600">
            {t("activities.email.resendCooldown", {
              minutes: Math.ceil(remainingCooldownMs / 60_000),
            })}
          </p>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <DialogFooter>
          <Button
            onClick={handleSubmit}
            disabled={isPending || isCoolingDown || !email}
          >
            {t("activities.email.resendSubmit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
